// src/utils/apiClient.js
import { apiUtils } from './api';
import storageHelper from './storageHelper';

const API_ROUTES = {
  BUSINESS: '/api/business',
  AI: '/api/ai',
  AUTH: '/auth',
  USER: '/api/user',
  AFFILIATE: '/api/ai/affiliate',
  MEDIA: '/api/ai/blog',
  BLOG: '/api/ai/blog' // Added affiliate base route to match Postman-tested endpoint
};

// Helper function to get auth header
const getAuthHeader = () => ({
  Authorization: `Bearer ${storageHelper.get('authToken')}`
});

export const apiClient = {
  businessIdeas: {
    create: async (ideaData) => {
      return apiUtils.makeRequest(`${API_ROUTES.BUSINESS}/idea`, {
        method: 'POST',
        body: JSON.stringify(ideaData),
        headers: getAuthHeader()
      });
    },
    generateSuggestion: async (suggestionData) => {
      return apiUtils.makeRequest(`${API_ROUTES.AI}/generate-suggestion`, {
        method: 'POST',
        body: JSON.stringify(suggestionData),
        headers: getAuthHeader()
      });
    },
    getAnalysis: async (businessId) => {
      return apiUtils.makeRequest(`${API_ROUTES.BUSINESS}/idea/${businessId}`, {
        method: 'GET',
        headers: getAuthHeader()
      });
    }
  },

  auth: {
    register: async (emailData) => {
      return apiUtils.makeRequest(`${API_ROUTES.AUTH}/register`, {
        method: 'POST',
        body: JSON.stringify(emailData)
      });
    },
    login: async (emailData) => {
      return apiUtils.makeRequest(`${API_ROUTES.AUTH}/login`, {
        method: 'POST',
        body: JSON.stringify(emailData)
      });
    },
    validateToken: async (tokenData) => {
      return apiUtils.makeRequest(`${API_ROUTES.AUTH}/validate-token`, {
        method: 'POST',
        body: JSON.stringify({ token: tokenData })
      });
    },
    updatePassword: async (passwordData) => {
      return apiUtils.makeRequest(`${API_ROUTES.AUTH}/set-password`, {
        method: 'POST',
        body: JSON.stringify(passwordData),
        headers: getAuthHeader()
      });
    },
    resendVerificationEmail: async (email) => {
      return apiUtils.makeRequest(`${API_ROUTES.AUTH}/resend-verification-email`, {
        method: 'POST',
        body: JSON.stringify(email)
      });
    },
    forgotPassword: async (emailData) => {
      return apiUtils.makeRequest(`${API_ROUTES.AUTH}/forgot-password`, {
        method: 'POST',
        body: JSON.stringify(emailData)
      });
    },
    resetPassword: async (tokenData) => {
      return apiUtils.makeRequest(`${API_ROUTES.AUTH}/reset-password`, {
        method: 'POST',
        body: JSON.stringify(tokenData)
      });
    },
    resendResetPasswordEmail: async (emailData) => {
      return apiUtils.makeRequest(`${API_ROUTES.AUTH}/resend-reset-password`, {
        method: 'POST',
        body: JSON.stringify(emailData)
      });
    }
  },

  user: {
    getProfile: async () => {
      return apiUtils.makeRequest(`${API_ROUTES.USER}/profile`, {
        method: 'GET',
        headers: getAuthHeader()
      });
    },
    updateAccount: async (accountData) => {
      return apiUtils.makeRequest(`${API_ROUTES.USER}/update-account`, {
        method: 'PUT',
        body: JSON.stringify(accountData),
        headers: getAuthHeader()
      });
    },
    updateAffiliate: async (affiliateData, token) => {
      return apiUtils.makeRequest(`${API_ROUTES.USER}/update-affiliate`, {
        method: 'POST',
        body: JSON.stringify(affiliateData),
        headers: {
          Authorization: `Bearer ${token ?? storageHelper.get('authToken')}`
        }
      });
    },
    uploadProfilePhoto: async (file) => {
      const formData = new FormData();
      formData.append('profile_photo', file);

      const headers = {
        Authorization: `Bearer ${storageHelper.get('authToken')}`
      };

      return apiUtils.makeRequest(`${API_ROUTES.USER}/profile-photo`, {
        method: 'POST',
        body: formData,
        headers
      });
    },
    deleteProfilePhoto: async () => {
      return apiUtils.makeRequest(`${API_ROUTES.USER}/profile-photo`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
    }
  },

  affiliate: {
    getCommissions: async (filters = {}) => {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value);
        }
      });
      const queryString = queryParams.toString();
      const url = queryString ? `${API_ROUTES.AFFILIATE}/commissions?${queryString}` : `${API_ROUTES.AFFILIATE}/commissions`;

      return apiUtils.makeRequest(url, {
        method: 'GET',
        headers: getAuthHeader()
      });
    },

    getCommissionById: async (commissionId) => {
      return apiUtils.makeRequest(`${API_ROUTES.AFFILIATE}/commissions/${commissionId}`, {
        method: 'GET',
        headers: getAuthHeader()
      });
    },

    getCommissionsByAffiliateId: async (affiliateId, filters = {}) => {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value);
        }
      });
      const queryString = queryParams.toString();
      const url = queryString ? `${API_ROUTES.AFFILIATE}/commissions?${queryString}` : `${API_ROUTES.AFFILIATE}/commissions`;

      return apiUtils.makeRequest(url, {
        method: 'GET',
        headers: getAuthHeader()
      });
    },

    createCommission: async (commissionData) => {
      return apiUtils.makeRequest(`${API_ROUTES.AFFILIATE}/commissions`, {
        method: 'POST',
        body: JSON.stringify(commissionData),
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        }
      });
    },

    updateCommission: async (commissionId, commissionData) => {
      return apiUtils.makeRequest(`${API_ROUTES.AFFILIATE}/commissions/${commissionId}`, {
        method: 'PUT',
        body: JSON.stringify(commissionData),
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        }
      });
    },

    deleteCommission: async (commissionId) => {
      return apiUtils.makeRequest(`${API_ROUTES.AFFILIATE}/commissions/${commissionId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
    },

    changeCommissionStatus: async (commissionId, status, notes = null) => {
      const payload = { status };
      if (notes) payload.notes = notes;

      return apiUtils.makeRequest(`${API_ROUTES.AFFILIATE}/commissions/${commissionId}/status`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        }
      });
    },

    getCommissionStats: async () => {
      return apiUtils.makeRequest(`${API_ROUTES.AFFILIATE}/commissions/stats`, {
        method: 'GET',
        headers: getAuthHeader()
      });
    },

    exportCommissionsReport: async (filters = {}) => {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value);
        }
      });
      const queryString = queryParams.toString();
      const url = queryString ? `${API_ROUTES.AFFILIATE}/commissions/export?${queryString}` : `${API_ROUTES.AFFILIATE}/commissions/export`;

      return apiUtils.makeRequest(url, {
        method: 'GET',
        headers: getAuthHeader()
      });
    },

    approveCommissions: async (commissionIds) => {
      return apiUtils.makeRequest(`${API_ROUTES.AFFILIATE}/commissions/approve-batch`, {
        method: 'POST',
        body: JSON.stringify({ commission_ids: commissionIds }),
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        }
      });
    },

    blog: {
      // Posts
      getPosts: async (filters = {}) => {
        console.log('calling');
        const queryParams = new URLSearchParams(filters).toString();
        const url = queryParams ? `${API_ROUTES.BLOG}/posts?${queryParams}` : `${API_ROUTES.BLOG}/posts`;
        return apiUtils.makeRequest(url, {
          method: 'GET',
          headers: getAuthHeader()
        });
      },

      getAdminPosts: async (filters = {}) => {
        filters.admin = 'true';
        const queryParams = new URLSearchParams(filters).toString();
        const url = queryParams ? `${API_ROUTES.BLOG}/posts?${queryParams}` : `${API_ROUTES.BLOG}/posts`;
        return apiUtils.makeRequest(url, {
          method: 'GET',
          headers: getAuthHeader()
        });
      },

      getPostBySlug: async (slug) => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/posts/slug/${slug}`, {
          method: 'GET',
          headers: getAuthHeader()
        });
      },

      createPost: async (postData) => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/posts`, {
          method: 'POST',
          body: JSON.stringify(postData),
          headers: getAuthHeader()
        });
      },

      updatePost: async (postId, postData) => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/posts/${postId}`, {
          method: 'PUT',
          body: JSON.stringify(postData),
          headers: getAuthHeader()
        });
      },

      deletePost: async (postId) => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/posts/${postId}`, {
          method: 'DELETE',
          headers: getAuthHeader()
        });
      },

      publishPost: async (postId) => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/posts/${postId}/publish`, {
          method: 'PUT',
          headers: getAuthHeader()
        });
      },

      unpublishPost: async (postId) => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/posts/${postId}/unpublish`, {
          method: 'PUT',
          headers: getAuthHeader()
        });
      },

      // Categories
      getCategories: async () => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/categories`, {
          method: 'GET',
          headers: getAuthHeader()
        });
      },

      createCategory: async (categoryData) => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/categories`, {
          method: 'POST',
          body: JSON.stringify(categoryData),
          headers: getAuthHeader()
        });
      },

      updateCategory: async (categoryId, categoryData) => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/categories/${categoryId}`, {
          method: 'PUT',
          body: JSON.stringify(categoryData),
          headers: getAuthHeader()
        });
      },

      deleteCategory: async (categoryId) => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/categories/${categoryId}`, {
          method: 'DELETE',
          headers: getAuthHeader()
        });
      },

      // Comments
      getComments: async (postId) => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/posts/${postId}/comments`, {
          method: 'GET',
          headers: getAuthHeader()
        });
      },

      getAllComments: async () => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/comments`, {
          method: 'GET',
          headers: getAuthHeader()
        });
      },

      submitComment: async (postId, commentData) => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/posts/${postId}/comments`, {
          method: 'POST',
          body: JSON.stringify(commentData),
          headers: getAuthHeader()
        });
      },

      approveComment: async (commentId) => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/comments/${commentId}/approve`, {
          method: 'PUT',
          headers: getAuthHeader()
        });
      },

      rejectComment: async (commentId) => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/comments/${commentId}/reject`, {
          method: 'PUT',
          headers: getAuthHeader()
        });
      },

      deleteComment: async (commentId) => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/comments/${commentId}`, {
          method: 'DELETE',
          headers: getAuthHeader()
        });
      },

      // Newsletter
      subscribeToNewsletter: async (email) => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/newsletter/subscribe`, {
          method: 'POST',
          body: JSON.stringify({ email }),
          headers: getAuthHeader()
        });
      },

      // Settings
      getBlogSettings: async () => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/settings`, {
          method: 'GET',
          headers: getAuthHeader()
        });
      },

      updateBlogSettings: async (settingsData) => {
        return apiUtils.makeRequest(`${API_ROUTES.BLOG}/settings`, {
          method: 'PUT',
          body: JSON.stringify(settingsData),
          headers: getAuthHeader()
        });
      }
    }
  },

  media: {
    uploadImageBlog: async (file) => {
      const formDataBlog = new FormData();
      formDataBlog.append('image', file);
      const headers = getAuthHeader();

      return apiUtils.makeRequest(`${API_ROUTES.MEDIA}/upload-image`, {
        method: 'POST',
        body: formDataBlog,
        headers
      });
    }
  }
};
